import { useCompanyClientData } from '@/composables/useCompanyClientData';
import { useCustomerEmployeeData } from '@/composables/useCustomerEmployeeData';
import {
    dedupeAssigneeIds,
    filterUserIdsWithoutEmployees,
    splitAssigneeIds,
    toAssigneeIds,
    type AssigneeEmployee,
} from '@/utils/voucherAssignees';
import { computed } from 'vue';

export type VoucherAssigneeOption = {
    id: string;
    label: string;
};

export function useVoucherAssigneeOptions(companyId?: number) {
    const { customerEmployees, isPlaceholderDataCustomerEmployees } =
        useCustomerEmployeeData(companyId);
    const { clientOptions, isPendingClientOptions } = useCompanyClientData(companyId);

    const employees = computed(() => (customerEmployees.value ?? []) as AssigneeEmployee[]);

    const assigneeOptions = computed<VoucherAssigneeOption[]>(() => {
        const employeeOptions = (customerEmployees.value ?? []).map(
            (employee: { id: number; full_name: string }) => ({
                id: `employee:${employee.id}`,
                label: employee.full_name,
            }),
        );

        const employeeUserIds = new Set(
            employees.value
                .map((employee) => employee.user_id)
                .filter((id): id is number => id != null),
        );

        const userOptions = (clientOptions.value ?? [])
            .filter((user: { id: number }) => !employeeUserIds.has(user.id))
            .map((user: { id: number; name: string }) => ({
                id: `user:${user.id}`,
                label: user.name,
            }));

        return [...employeeOptions, ...userOptions];
    });

    const dedupeAssigneeIdsForCompany = (assigneeIds: string[] = []) =>
        dedupeAssigneeIds(assigneeIds, employees.value);

    const toAssigneeIdsForCompany = (employeeIds: number[] = [], userIds: number[] = []) =>
        toAssigneeIds(employeeIds, userIds, employees.value);

    const isLoadingAssigneeOptions = computed(
        () => isPlaceholderDataCustomerEmployees.value || isPendingClientOptions.value,
    );

    return {
        assigneeOptions,
        isLoadingAssigneeOptions,
        toAssigneeIds: toAssigneeIdsForCompany,
        dedupeAssigneeIds: dedupeAssigneeIdsForCompany,
        filterUserIdsWithoutEmployees: (userIds: number[] = []) =>
            filterUserIdsWithoutEmployees(userIds, employees.value),
        splitAssigneeIds,
    };
}
